import React, { useRef, useState } from 'react'

// Libraries
import { Col, Container, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { Parallax } from 'react-scroll-parallax';
import { AnimatePresence, m } from 'framer-motion'
import { Form, Formik } from 'formik';

// Components
import { fancyTextBox02 } from '../../Components/FancyTextBox/FancyTextBoxData';
import FancyTextBox from '../../Components/FancyTextBox/FancyTextBox';
import { ContactFormStyle03Schema } from '../../Components/Form/FormSchema';
import { Checkbox, Input, TextArea } from '../../Components/Form/Form'
import { fadeIn } from '../../Functions/GlobalAnimations';
import MessageBox from '../../Components/MessageBox/MessageBox';
import Buttons from '../../Components/Button/Buttons'
import { resetForm, sendEmail } from '../../Functions/Utilities';
import { Shield, MapPinned, Users, Mail, Phone } from "lucide-react"


const Contact = (props) => {
  const formRef = useRef(null);

  // États du formulaire
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    comment: '',
    terms_condition: false,
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  // Gestion des changements
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  // Validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Nom est requis';
    if (!formData.email.trim()) {
      newErrors.email = 'Email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email invalide';
    }
    if (!formData.phone.trim()) newErrors.phone = 'Le téléphone est requis';
    if (!formData.comment.trim()) newErrors.comment = 'Le commentaire est requis';
    if (!formData.terms_condition) newErrors.terms_condition = 'Les conditions doivent être acceptées';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Soumission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsSubmitting(true);
    setStatus(null);

    try {
      const response = await sendEmail(formData);
      if (response.status === 'success') {
        setStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          comment: '',
          terms_condition: false,
        });
        setErrors({});
        formRef.current.reset();
      } else {
        setStatus('error');
      }
    } catch (err) {
      setStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };
  const form = useRef(null)

  // بيانات IconWithText مترجمة
  const IconWithTextData = [
    {
      icon: Mail,
    titleKey: 'Comment pouvons-nous vous aider?',
    descriptionKey: (
      <>
        secretariat@speedtunisia.com
        <br />
        direction@speedtunisia.com
      </>
    ),
    linkTitleKey: 'Envoyez-nous un e-mail',
    link: 'mailto:secretariat@speedtunisia.com',
    },
    {
      icon: Phone,
    titleKey: 'N\'hésitez pas à nous contacter ?',
    descriptionKey: (
      <>
        (+216) 73 29 88 55
        <br />
        (+216) 29 465 465
      </>
    ),
    linkTitleKey: 'Appelez-nous dès aujourd\'hui',
    link: '#',
    },
    {
      icon: MapPinned,
    titleKey: 'Siège Social',
    descriptionKey: '163, Av H.Bourguiba. 4021 Kalaa Esseghira Sousse,Tunisie',
    linkTitleKey: 'Visitez notre siège social',
    link: '#',
    },
  ];

  return (
    <div style={props.style}>
       {/* Parallax Scrolling Start */}
       <m.div className="h-[380px] overflow-hidden sm:h-[200px] relative" {...fadeIn}>
                            <Parallax 
                                className="lg-no-parallax bg-cover absolute top-0 left-0 w-full h-full" 
                                translateY={[-40, 40]} 
                                style={{ backgroundImage: `url(/assets/img/webp/portfolio-bg.webp)` }}
                            />
                            <Container className="h-full relative">
                                <Row className="items-stretch justify-center h-[450px] md:h-[375px] sm:h-[275px]">
                                    <Col xl={6} lg={7} md={8} xs={12} className="text-center flex justify-center flex-col">
                                        <h2 className="text-darkgray mb-[0px] font-serif font-medium -tracking-[1px] xs:mb-[0px]">Contact</h2>
                                    </Col>
                                </Row>
                            </Container>
                        </m.div>
      {/* Parallax Scrolling End */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid min-md:grid-cols-3 gap-8">
            {IconWithTextData.map((feature, index) => {
              const Icon = feature.icon
              return (
                <div
                  key={index}
                  className="bg-white p-8 rounded-lg shadow-sm hover:shadow-md transition-shadow animate-fade-in-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="mb-4">
                    <Icon size={48} style={{ color: "#558927" }} className="mx-auto" />
                  </div>
                  <h3 className="text-lg font-bold mb-3 text-center" style={{ color: "#87c03d" }}>
                    {feature.titleKey}
                  </h3>
                  <p className="text-gray-600 text-center text-xmd">{feature.descriptionKey}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <m.section className="py-24 min-lg:py-20 min-md:py-16 min-sm:py-12" {...fadeIn}>
        <Container>
          <Row className="justify-center">
            <Col xl={6} lg={7} className="text-center mb-16 min-md:mb-12">
              <span className="font-serif mb-2 -tracking-tight text-lg block text-[#87c03d]">
                Remplissez le formulaire et nous vous contacterons bientôt !
              </span>
              <h4 className="font-serif font-semibold text-3xl min-md:text-4xl text-[#558927]">
                Comment pouvons-nous vous aider ?
              </h4>
            </Col>

            <Col xl={10} className="col-sm-12 col-xs-12">
              <form ref={formRef} onSubmit={handleSubmit}>
                <Row className="grid grid-cols-1 min-md:grid-cols-2 gap-y-6 min-md:gap-y-8">
                  {/* Colonne gauche */}
                  <Col>
                    <div className="mb-6">
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Saisir votre Nom"
                        className="py-4 px-5 text-base w-full border border-[#d1d9e6] rounded-lg focus:border-[#558927] focus:ring-2 focus:ring-[#558927]/30 transition-all duration-300"
                      />
                      {errors.name && <p className="text-[#558927] text-sm mt-1.5">{errors.name}</p>}
                    </div>

                    <div className="mb-6">
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Saisir votre Email"
                        className="py-4 px-5 text-base w-full border border-[#d1d9e6] rounded-lg focus:border-[#558927] focus:ring-2 focus:ring-[#558927]/30 transition-all duration-300"
                      />
                      {errors.email && <p className="text-[#558927] text-sm mt-1.5">{errors.email}</p>}
                    </div>

                    <div className="mb-6 min-md:mb-0">
                      <input
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Saisir votre Téléphone"
                        className="py-4 px-5 text-base w-full border border-[#d1d9e6] rounded-lg focus:border-[#558927] focus:ring-2 focus:ring-[#558927]/30 transition-all duration-300"
                      />
                      {errors.phone && <p className="text-[#558927] text-sm mt-1.5">{errors.phone}</p>}
                    </div>
                  </Col>

                  {/* Colonne droite */}
                  <Col>
                    <div className="mb-6 min-md:mb-0">
                      <textarea
                        name="comment"
                        value={formData.comment}
                        onChange={handleChange}
                        rows="7"
                        placeholder="Saisir votre Commentaire"
                        className="border border-[#d1d9e6] w-full py-4 px-5 text-base h-[220px] resize-none rounded-lg focus:border-[#558927] focus:ring-2 focus:ring-[#558927]/30 transition-all duration-300"
                      />
                      {errors.comment && <p className="text-[#558927] text-sm mt-1.5">{errors.comment}</p>}
                    </div>
                  </Col>

                  {/* Checkbox CGU */}
                  <Col className="text-left">
                    <label className="flex items-start cursor-pointer gap-3">
                      <input
                        type="checkbox"
                        name="terms_condition"
                        checked={formData.terms_condition}
                        onChange={handleChange}
                        className="mt-1.5 h-5 w-5 accent-[#558927]"
                      />
                      <span className="text-sm leading-relaxed text-[#558927]">
                        J'accepte les conditions générales d'utilisation
                      </span>
                    </label>
                    {errors.terms_condition && (
                      <p className="text-[#558927] text-sm mt-1.5">{errors.terms_condition}</p>
                    )}
                  </Col>

                  {/* Bouton Envoyer */}
                  <Col className="text-right">
                    <Buttons
                      type="submit"
                      disabled={isSubmitting}
                      className={`btn-fill font-medium font-serif rounded-lg uppercase tracking-wide px-10 py-3.5 text-base transition-all duration-300 ${
                        isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:brightness-110 hover:shadow-lg'
                      }`}
                      themeColor="#558927"
                      color="#fff"
                      size="md"
                      title={isSubmitting ? 'Envoi en cours...' : 'Envoyer'}
                    />
                  </Col>
                </Row>

                {/* Message de succès/erreur */}
                <AnimatePresence>
                  {status && (
                    <Row className="mt-10">
                      <Col xs={12}>
                        <m.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                          <MessageBox
                            className="py-3 px-5 rounded-lg border-l-4"
                            theme="message-box01"
                            variant={status === 'success' ? 'success' : 'danger'}
                            style={{
                              backgroundColor: status === 'success' ? '#e6f7ff' : '#fff5f5',
                              borderLeftColor: status === 'success' ? '#58bceb' : '#558927',
                              color: status === 'success' ? '#003479' : '#003479',
                            }}
                            message={
                              status === 'success'
                                ? 'Message envoyé avec succès !'
                                : "Une erreur s'est produite lors de l'envoi du message."
                            }
                          />
                        </m.div>
                      </Col>
                    </Row>
                  )}
                </AnimatePresence>
              </form>
            </Col>
          </Row>
        </Container>
      </m.section>
      <iframe 
      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3234.571031012416!2d10.556550276421222!3d35.8349991725381!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x12fd8b0ecbcad1cf%3A0xab81a9f8d26d4f5!2sSPEED%20TUNISIA!5e0!3m2!1sfr!2sus!4v1750770947188!5m2!1sfr!2sus" 
      width="600" height="450" 
      allowfullscreen="" loading="lazy" referrerpolicy="no-referrer-when-downgrade"></iframe>

    </div>
  )
}

export default Contact